import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import {
  Receipt, Plus, MagnifyingGlass, TrendDown, ChartPieSlice, CurrencyInr,
  PencilSimple, Trash, ArrowsClockwise, Buildings,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const PIE_COLORS = ["#0891b2", "#059669", "#d97706", "#dc2626", "#4f46e5", "#7c3aed", "#db2777", "#65a30d", "#9333ea", "#0284c7"];

const CATEGORY_ICONS = {
  "Office Rent": "🏢", "Salaries": "👨‍💼", "Electricity": "⚡", "Internet": "🌐",
  "Telephone": "📞", "Printing": "🖨️", "Stationery": "📄", "Travel": "🚗",
  "Office Refreshments": "☕", "Maintenance": "🛠️", "Advertising & Marketing": "📢",
  "Housekeeping": "🧹", "Courier": "📦", "Miscellaneous": "📋",
};

const EMPTY = {
  date: new Date().toISOString().slice(0, 10),
  category: "Office Rent",
  vendor: "",
  amount: "",
  payment_mode: "Cash",
  gst: false,
  gst_percent: 18,
  gst_amount: 0,
  bill_no: "",
  remarks: "",
};

export default function Expenses() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [finance, setFinance] = useState(null);
  const [cats, setCats] = useState({ preset: [], custom: [], payment_modes: ["Cash", "UPI", "Card", "Bank Transfer", "Cheque"] });
  const [q, setQ] = useState("");
  const [fCat, setFCat] = useState("all");
  const [fMode, setFMode] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadStats = useCallback(() => api.get("/expenses/stats").then((r) => setStats(r.data)).catch(() => {}), []);
  const loadFinance = useCallback(() => api.get("/finance/summary").then((r) => setFinance(r.data)).catch(() => {}), []);
  const loadCats = useCallback(() => api.get("/expenses/categories").then((r) => setCats(r.data)).catch(() => {}), []);
  const loadRows = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (fCat !== "all") params.category = fCat;
    if (fMode !== "all") params.payment_mode = fMode;
    return api.get("/expenses", { params }).then((r) => setRows(r.data)).catch(() => {});
  }, [q, fCat, fMode]);

  useEffect(() => { loadStats(); loadFinance(); loadCats(); loadRows(); }, [loadStats, loadFinance, loadCats, loadRows]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      date: row.date, category: row.category, vendor: row.vendor,
      amount: row.amount, payment_mode: row.payment_mode, gst: row.gst,
      gst_percent: row.gst_percent, gst_amount: row.gst_amount,
      bill_no: row.bill_no, remarks: row.remarks,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter a valid amount"); return; }
    if (!form.category) { toast.error("Pick a category"); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount), gst_percent: Number(form.gst_percent) || 0, gst_amount: Number(form.gst_amount) || 0 };
      if (editing) {
        await api.patch(`/expenses/${editing.id}`, payload);
        toast.success("Expense updated");
      } else {
        await api.post("/expenses", payload);
        toast.success("Expense saved");
      }
      setOpen(false);
      await Promise.all([loadStats(), loadFinance(), loadRows(), loadCats()]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const removeRow = async (row) => {
    if (!confirm(`Delete expense ${row.expense_no}?`)) return;
    try {
      await api.delete(`/expenses/${row.id}`);
      toast.success("Expense deleted");
      await Promise.all([loadStats(), loadFinance(), loadRows()]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const allCats = [...cats.preset, ...cats.custom];

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-rose-600 font-semibold mb-2 flex items-center gap-2">
            <TrendDown size={14} weight="fill" /> Financials
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Expenses</h1>
          <p className="text-sm text-slate-600 mt-1.5">Track office expenses, calculate net income & find leaks.</p>
        </div>
        <Button onClick={openNew} data-testid="exp-new-btn" className="gap-2 bg-rose-600 hover:bg-rose-700 text-white">
          <Plus size={16} weight="bold" /> Add Expense
        </Button>
      </div>

      {/* P&L cards */}
      {finance && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="exp-finance-row">
          <PnlCard label="Today's Revenue" value={inr(finance.revenue_today)} tone="emerald" />
          <PnlCard label="Today's Expense" value={inr(finance.expense_today)} tone="rose" />
          <PnlCard label="Net Today" value={inr(finance.net_today)} tone={finance.net_today >= 0 ? "emerald" : "rose"} big />
          <PnlCard label="Month Net" value={inr(finance.net_month)} tone={finance.net_month >= 0 ? "emerald" : "rose"} />
        </div>
      )}

      {/* Expense-only stats */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="p-4 bg-white space-y-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Totals</div>
            <Row label="Today's Expense" value={inr(stats.today)} />
            <Row label="This Month" value={inr(stats.month)} />
            <Row label="All-time" value={inr(stats.total)} bold />
          </Card>

          <Card className="p-4 bg-white">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
              <ChartPieSlice size={12} weight="fill" /> By Category
            </div>
            {stats.by_category.length === 0 ? (
              <EmptyChart />
            ) : (
              <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={stats.by_category} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={70}>
                      {stats.by_category.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => inr(v)} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          <Card className="p-4 bg-white">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">By Payment Mode</div>
            {stats.by_payment_mode.length === 0 ? <EmptyChart /> : (
              <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <BarChart data={stats.by_payment_mode}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="mode" fontSize={10} />
                    <YAxis fontSize={10} />
                    <Tooltip formatter={(v) => inr(v)} />
                    <Bar dataKey="amount" fill="#0891b2" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* List */}
      <Card className="p-4 bg-white">
        <div className="flex flex-wrap gap-2 items-center mb-3">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="exp-search" placeholder="Search expense no / vendor / remarks…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9 text-sm" />
          </div>
          <Select value={fCat} onValueChange={setFCat}>
            <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="exp-filter-cat"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {allCats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fMode} onValueChange={setFMode}>
            <SelectTrigger className="h-9 w-[140px] text-sm" data-testid="exp-filter-mode"><SelectValue placeholder="Mode" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All modes</SelectItem>
              {cats.payment_modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={loadRows} className="h-9 gap-1.5"><ArrowsClockwise size={13} /> Refresh</Button>
        </div>

        {rows.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-sm">No expenses yet. Click <b>Add Expense</b> to record your first.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 pr-3">Exp #</th>
                  <th className="text-left py-2 pr-3">Date</th>
                  <th className="text-left py-2 pr-3">Category</th>
                  <th className="text-left py-2 pr-3">Vendor / Payee</th>
                  <th className="text-left py-2 pr-3">Mode</th>
                  <th className="text-right py-2 pr-3">Amount</th>
                  <th className="text-right py-2 pr-3">Net</th>
                  <th className="text-right py-2 pr-1">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`exp-row-${r.expense_no}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 pr-3 font-mono text-xs text-rose-700">{r.expense_no}</td>
                    <td className="py-2 pr-3 text-slate-700">{r.date}</td>
                    <td className="py-2 pr-3">
                      <span className="text-slate-800">{CATEGORY_ICONS[r.category] || "•"} {r.category}</span>
                    </td>
                    <td className="py-2 pr-3 text-slate-700">{r.vendor || <span className="text-slate-400">—</span>}</td>
                    <td className="py-2 pr-3"><Badge variant="outline" className="text-[10px]">{r.payment_mode}</Badge></td>
                    <td className="py-2 pr-3 text-right font-medium">{inr(r.amount)}</td>
                    <td className="py-2 pr-3 text-right font-semibold text-slate-900">{inr(r.net_amount)}{r.gst ? <span className="ml-1 text-[10px] text-slate-400">(incl GST)</span> : null}</td>
                    <td className="py-2 pr-1 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(r)} data-testid={`exp-edit-${r.expense_no}`} className="text-indigo-600 hover:bg-indigo-50 p-1 rounded"><PencilSimple size={14} weight="bold" /></button>
                      <button onClick={() => removeRow(r)} data-testid={`exp-del-${r.expense_no}`} className="text-rose-600 hover:bg-rose-50 p-1 rounded ml-1"><Trash size={14} weight="bold" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.expense_no}` : "Add Expense"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date *"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="exp-date" /></Field>
            <Field label="Category *">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="exp-cat"><SelectValue /></SelectTrigger>
                <SelectContent>{allCats.map((c) => <SelectItem key={c} value={c}>{CATEGORY_ICONS[c] || "•"} {c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Vendor / Payee"><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="exp-vendor" placeholder="e.g. MSEB, Vodafone" /></Field>
            <Field label="Amount *"><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="exp-amount" /></Field>
            <Field label="Payment Mode *">
              <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                <SelectTrigger data-testid="exp-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.payment_modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Bill / Ref No"><Input value={form.bill_no} onChange={(e) => setForm({ ...form, bill_no: e.target.value })} placeholder="Optional" /></Field>
            <div className="col-span-2 flex items-center gap-2 border-t border-slate-100 pt-2">
              <Checkbox id="exp-gst" checked={form.gst} onCheckedChange={(v) => setForm({ ...form, gst: !!v })} data-testid="exp-gst-cb" />
              <label htmlFor="exp-gst" className="text-sm">GST applicable</label>
              {form.gst && (
                <>
                  <Input type="number" className="h-8 w-20 ml-3" placeholder="%" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} />
                  <span className="text-xs text-slate-500">%</span>
                  <Input type="number" className="h-8 w-24 ml-2" placeholder="GST ₹" value={form.gst_amount} onChange={(e) => setForm({ ...form, gst_amount: e.target.value })} />
                </>
              )}
            </div>
            <Field label="Remarks" full><Textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="Notes / description" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="exp-save-btn">
              {saving ? "Saving…" : editing ? "Update Expense" : "Save Expense"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">{label}</div>
      {children}
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={`${bold ? "font-bold text-slate-900" : "font-medium text-slate-800"}`}>{value}</span>
    </div>
  );
}

function PnlCard({ label, value, tone, big }) {
  const tones = {
    emerald: "border-l-emerald-500 bg-emerald-50/40",
    rose: "border-l-rose-500 bg-rose-50/40",
    indigo: "border-l-indigo-500 bg-indigo-50/40",
  };
  return (
    <Card className={`p-3 border-l-4 ${tones[tone] || tones.indigo}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
      <div className={`${big ? "text-3xl" : "text-2xl"} font-bold text-slate-900 mt-0.5`} style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </Card>
  );
}

function EmptyChart() {
  return <div className="h-40 flex items-center justify-center text-sm text-slate-400">No data yet</div>;
}
